import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { fmtNum, LOCATION_LABELS } from "../lib/format";
import { Badge } from "../components/ui/badge";
import ExportButtons from "../components/ExportButtons";
import PrintFrame from "../components/PrintFrame";
import ItemHistoryModal from "../components/ItemHistoryModal";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const COLS = [
  ["STORE_CLEAN", "Store Clean"],
  ["ROOM_CLEAN", "Rooms"],
  ["DIRTY_CHECKOUT", "Dirty Checkout"],
  ["DIRTY_HOLDING", "Dirty Holding"],
  ["EXTERNAL_LAUNDRY", "At Laundry"],
  ["RETURNED_AWAITING", "Returned"],
  ["DAMAGED_QUARANTINE", "Damaged"],
];

export default function MasterStock() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [historyItem, setHistoryItem] = useState(null);

  const loadRows = useCallback(async () => {
    const { data } = await api.get("/reports/master-stock");
    setRows(data.rows || []);
  }, []);

  useEffect(() => {
    loadRows().catch(() => setRows([]));
  }, [loadRows]);

  useLiveRefresh(loadRows);

  const filtered = rows.filter(r => {
    const s = q.toLowerCase();
    return !s || r.name.toLowerCase().includes(s);
  });

  const totals = filtered.reduce((acc, r) => {
    COLS.forEach(([k]) => { acc[k] = (acc[k] || 0) + (r[k] || 0); });
    acc.accountable_total = (acc.accountable_total || 0) + r.accountable_total;
    acc.available_stock = (acc.available_stock || 0) + r.available_stock;
    return acc;
  }, {});

  return (
    <Layout>
      <PrintFrame title="Master Linen Stock" subtitle="Consolidated stock by location" user={user}>
        <div className="mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3 no-print">
          <div>
            <h1 className="section-title" data-testid="page-title">{t("nav.master_stock")}</h1>
            <div className="section-sub">{t("sub.master_stock")}</div>
          </div>
          <div className="flex items-center gap-3">
            <Input placeholder="Search item…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 w-64" data-testid="stock-search" />
            <ExportButtons endpoint="/reports/export/master-stock" filenameBase="master-stock" />
          </div>
        </div>

        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="vr-table" data-testid="master-stock-table">
              <thead>
                <tr>
                  <th>Item</th>
                  {COLS.map(([_, l]) => <th key={_} className="num">{l}</th>)}
                  <th className="num">Accountable</th>
                  <th className="num">Available</th>
                  <th className="num">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.item_id} data-testid={`stock-row-${r.name.toLowerCase().replace(/\s+/g,"-")}`}
                      className="cursor-pointer" onClick={() => setHistoryItem(r)}>
                    <td className="font-medium text-slate-800 underline decoration-slate-300 underline-offset-2">{r.name}</td>
                    {COLS.map(([k]) => <td key={k} className="num">{fmtNum(r[k])}</td>)}
                    <td className="num font-semibold">{fmtNum(r.accountable_total)}</td>
                    <td className="num">{fmtNum(r.available_stock)}</td>
                    <td className="num">
                      {r.low_stock ? <Badge className="bg-amber-500/90 text-white">low · {r.low_stock_threshold}</Badge> : <span className="text-slate-500">{r.low_stock_threshold}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={1}>Totals ({filtered.length} items)</td>
                  {COLS.map(([k]) => <td key={k} className="num">{fmtNum(totals[k])}</td>)}
                  <td className="num">{fmtNum(totals.accountable_total)}</td>
                  <td className="num">{fmtNum(totals.available_stock)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </PrintFrame>
      <ItemHistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />
    </Layout>
  );
}
